// Estados da conferência de rebate do ML (objeto `rebate` que o backend
// devolve na lista e na análise da order). O backend decide o estado; aqui só
// fica o vocabulário visual. Os oito estados espelham o contrato wire —
// 'divergente' e 'nao_conferivel' deixaram de existir quando a conferência
// passou a ler também o desconto na tarifa (fee_discount) do extrato;
// 'recebido_sem_historico' entrou para separar, dentro do que era "fora da
// cobertura", os pagamentos em que o ML de fato aportou algo.

// Ordem de prioridade na consolidação por order (o pior estado ganha) — a
// mesma que o backend usa, para a UI ordenar/filtrar sem reinventar.
export const REBATE_STATE_ORDER = [
    'faltou',
    'a_mais',
    'aporte_sem_campanha',
    'recebido_sem_historico',
    'pendente',
    'conferido',
    'em_disputa',
    'cancelado',
    'sem_rebate',
    'sem_cobertura',
];

// Todo par texto/fundo abaixo fecha ≥ 4.5:1 (WCAG AA) nos dois temas — por
// isso os pontos saturados usam o tom 700 (500 com branco fica em ~3:1).
// `dot` é o ponto da lista, `chip` a pastilha da análise da order, `text` a
// cor do valor/diferença escrito ao lado do ponto.
export const REBATE_STATE_STYLE = {
    sem_cobertura: {
        label: 'Fora da cobertura',
        glyph: '?',
        dot: 'bg-mono-300 text-mono-600 dark:bg-mono-600 dark:text-mono-200',
        chip: 'border-mono-300 bg-mono-100 text-mono-600 dark:border-mono-600 dark:bg-mono-800 dark:text-mono-300',
        text: 'text-mono-500 dark:text-mono-400',
        hint: 'Sem anúncio ou venda anterior ao histórico de campanhas — não dá para saber o que foi prometido.',
    },
    em_disputa: {
        label: 'Em disputa',
        icon: 'clock',
        dot: 'bg-mono-500 text-white dark:bg-mono-400 dark:text-mono-900',
        chip: 'border-mono-400 bg-mono-100 text-mono-700 dark:border-mono-500 dark:bg-mono-800 dark:text-mono-200',
        text: 'text-mono-600 dark:text-mono-300',
        hint: 'Há reclamação/mediação aberta no extrato ou o pagamento está em mediação: o veredito fica suspenso até o dinheiro assentar.',
    },
    cancelado: {
        label: 'Cancelado / devolvido',
        glyph: '×',
        dot: 'bg-mono-300 text-mono-700 dark:bg-mono-600 dark:text-mono-100',
        chip: 'border-mono-300 bg-mono-100 text-mono-600 dark:border-mono-600 dark:bg-mono-800 dark:text-mono-300',
        text: 'text-mono-500 dark:text-mono-400',
        hint: 'Venda desfeita (estorno, devolução ou reclamação decidida pro comprador, pedido cancelado ou pagamento recusado): tarifa e aporte do ML foram revertidos — não há rebate a conferir.',
    },
    sem_rebate: {
        label: 'Sem rebate previsto',
        glyph: '–',
        dot: 'bg-mono-500 text-white',
        chip: 'border-mono-300 bg-mono-100 text-mono-700 dark:border-mono-600 dark:bg-mono-800 dark:text-mono-200',
        text: 'text-mono-500 dark:text-mono-400',
        hint: 'O anúncio não estava em campanha com aporte do Mercado Livre nesta data e o ML não devolveu nada.',
    },
    pendente: {
        label: 'Aguardando extrato',
        icon: 'clock',
        dot: 'bg-blue-600 text-white',
        chip: 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-300',
        text: 'text-blue-700 dark:text-blue-300',
        hint: 'Havia campanha com aporte do ML, mas o relatório do MercadoPago ainda não cobre este pagamento.',
    },
    conferido: {
        label: 'Rebate confere',
        icon: 'check',
        dot: 'bg-green-700 text-white',
        chip: 'border-green-300 bg-green-50 text-green-800 dark:border-green-700 dark:bg-green-950 dark:text-green-300',
        text: 'text-green-800 dark:text-green-300',
        hint: 'O que o ML devolveu (cupom + desconto na tarifa) bate com o prometido pelas campanhas do anúncio.',
    },
    faltou: {
        label: 'Faltou rebate',
        icon: 'warning',
        dot: 'bg-[#FBEAEA] text-[#9B2C2C] ring-1 ring-inset ring-[#9B2C2C]/40 dark:bg-red-950 dark:text-red-300 dark:ring-red-400/40',
        chip: 'border-[#9B2C2C]/40 bg-[#FBEAEA] text-[#9B2C2C] dark:border-red-700 dark:bg-red-950 dark:text-red-300',
        text: 'text-[#9B2C2C] dark:text-red-300',
        hint: 'O ML devolveu menos do que as campanhas do anúncio prometiam para esta venda.',
    },
    a_mais: {
        label: 'Rebate a mais',
        glyph: '+',
        dot: 'bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-500/50 dark:bg-amber-950 dark:text-amber-200 dark:ring-amber-400/40',
        chip: 'border-amber-400 bg-amber-100 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200',
        text: 'text-amber-900 dark:text-amber-200',
        hint: 'O ML devolveu mais do que as campanhas do anúncio prometiam — parte do aporte não tem campanha correspondente.',
    },
    aporte_sem_campanha: {
        label: 'ML pagou sem campanha conhecida',
        glyph: '!',
        dot: 'bg-amber-700 text-white',
        chip: 'border-amber-600 bg-amber-50 text-amber-900 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-200',
        text: 'text-amber-900 dark:text-amber-200',
        hint: 'O ML aportou nesta venda, mas nenhuma campanha do anúncio na data explica o valor.',
    },
    // Âmbar mais claro que 'a_mais' e sem o ring: é dinheiro do ML que ENTROU,
    // então não pode ficar cinza como 'sem_cobertura' — mas também não é um
    // alerta, porque não há prometido para comparar.
    recebido_sem_historico: {
        label: 'Recebido, sem histórico',
        glyph: '?',
        dot: 'bg-amber-200 text-amber-900 dark:bg-amber-900 dark:text-amber-100',
        chip: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200',
        text: 'text-amber-900 dark:text-amber-200',
        hint: 'O ML aportou (cupom e/ou desconto na tarifa), mas não há anúncio ligado ou histórico de campanhas na data para conferir se era o prometido.',
    },
};

// Opções do select de rebate da lista de pagamentos — a ordem é a de
// prioridade, para o pior estado aparecer primeiro, e o rótulo é o mesmo do
// ponto/pastilha, para a pessoa filtrar pelo que leu na linha.
export const REBATE_FILTER_OPTIONS = [
    { value: '', label: 'Rebate: todos' },
    ...REBATE_STATE_ORDER.map((s) => ({ value: s, label: REBATE_STATE_STYLE[s].label })),
];

export const RebateStyle = (state) => REBATE_STATE_STYLE[state] || REBATE_STATE_STYLE.sem_cobertura;
